import { useMemo } from 'react'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Loader2 } from 'lucide-react'

import { Input } from '@/components/shadcn/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { BankReconReferenceIndex } from './BankReconReferenceIndex'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/utils'

import {
  BankReconAmountEditPayload,
  BankReconColumnId,
  BankReconRow,
  BankReconTableData,
  BankReconTableUISchema,
} from './types'
import {
  amountsEqual,
  buildFormulaTooltip,
  buildReferenceIndex,
  formatBankReconAmount,
  getColumnLabel,
  parseEditableAmount,
} from './bankReconUtils'

interface BankReconTableProps {
  tableUi: BankReconTableUISchema
  tableData: BankReconTableData
  serverAmountByRef: Record<string, number>
  amountFormat?: string
  submitHandlerId?: string
  onAmountEdit?: (payload: BankReconAmountEditPayload) => void
  isSubmitting?: boolean
  pendingEditRef?: string | null
  /** When true, omit the table title (e.g. when the title is already shown as a tab label). */
  hideTitle?: boolean
}

function AmountCell({
  row,
  tableId,
  serverAmount,
  amountFormat,
  submitHandlerId,
  onAmountEdit,
  isSubmitting,
  pendingEditRef,
  emphasized,
}: {
  row: BankReconRow
  tableId: string
  serverAmount: number
  amountFormat?: string
  submitHandlerId?: string
  onAmountEdit?: (payload: BankReconAmountEditPayload) => void
  isSubmitting?: boolean
  pendingEditRef?: string | null
  emphasized?: boolean
}) {
  const isEditable = row.editable && submitHandlerId && onAmountEdit
  const isPending = isSubmitting && pendingEditRef === row.ref
  const displayValue = formatBankReconAmount(row.amount, amountFormat)

  const commitAmount = (raw: string) => {
    const parsed = parseEditableAmount(raw)
    if (parsed === null || amountsEqual(parsed, serverAmount)) return
    onAmountEdit?.({ tableId, ref: row.ref, amount: parsed })
  }

  if (!isEditable) {
    return <span className={cn('tabular-nums', emphasized && 'font-semibold')}>{displayValue}</span>
  }

  return (
    <div className="relative min-w-[7rem]">
      <Input
        key={`${row.ref}-${serverAmount}`}
        type="text"
        inputMode="decimal"
        defaultValue={displayValue}
        disabled={isPending}
        aria-label={`Edit amount for ${row.ref}`}
        className={cn(
          'h-8 border-2 border-primary/40 bg-background px-2 py-1 text-right text-sm tabular-nums shadow-sm',
          emphasized && 'font-semibold'
        )}
        onBlur={(event) => {
          commitAmount(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur()
            return
          }
          if (event.key === 'Tab') {
            commitAmount(event.currentTarget.value)
          }
        }}
      />
      {isPending && (
        <Loader2
          className="absolute right-2 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
          aria-hidden
        />
      )}
    </div>
  )
}

function FormulaCell({ row, amountFormat }: { row: BankReconRow; amountFormat?: string }) {
  if (!row.formula) return <span className="text-muted-foreground">—</span>

  const tooltip = buildFormulaTooltip(row.formula, amountFormat)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="max-w-full truncate text-left text-sm text-foreground underline decoration-dotted underline-offset-2 hover:text-primary"
        >
          {row.formula.expression}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm whitespace-pre-line text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

function renderCell(
  columnId: BankReconColumnId,
  row: BankReconRow,
  ctx: {
    tableId: string
    serverAmountByRef: Record<string, number>
    amountFormat?: string
    submitHandlerId?: string
    onAmountEdit?: (payload: BankReconAmountEditPayload) => void
    isSubmitting?: boolean
    pendingEditRef?: string | null
  }
) {
  switch (columnId) {
    case 'bucket':
      return row.bucket ?? '—'
    case 'classification':
      return row.classification ?? '—'
    case 'amount':
      return (
        <AmountCell
          row={row}
          tableId={ctx.tableId}
          serverAmount={ctx.serverAmountByRef[row.ref] ?? row.amount}
          amountFormat={ctx.amountFormat}
          submitHandlerId={ctx.submitHandlerId}
          onAmountEdit={ctx.onAmountEdit}
          isSubmitting={ctx.isSubmitting}
          pendingEditRef={ctx.pendingEditRef}
          emphasized={row.isTotal}
        />
      )
    case 'ref':
      return <span className="font-medium">{row.ref}</span>
    case 'formula':
      return <FormulaCell row={row} amountFormat={ctx.amountFormat} />
    default:
      return '—'
  }
}

export function BankReconTable({
  tableUi,
  tableData,
  serverAmountByRef,
  amountFormat,
  submitHandlerId,
  onAmountEdit,
  isSubmitting,
  pendingEditRef,
  hideTitle = false,
}: BankReconTableProps) {
  const rows = tableData.rows
  const referenceIndex = buildReferenceIndex(rows, tableData.referenceIndex)
  const showReferenceIndex = tableUi.showReferenceIndex && referenceIndex.length > 0
  const showHeader = (!hideTitle && !!tableUi.title) || !!tableUi.description

  const columns = useMemo<ColumnDef<BankReconRow>[]>(
    () =>
      tableUi.columns.map((columnId) => ({
        id: columnId,
        accessorKey: columnId,
        header: () => getColumnLabel(tableUi, columnId),
        cell: ({ row }) =>
          renderCell(columnId, row.original, {
            tableId: tableUi.id,
            serverAmountByRef,
            amountFormat,
            submitHandlerId,
            onAmountEdit,
            isSubmitting,
            pendingEditRef,
          }),
      })),
    [tableUi, serverAmountByRef, amountFormat, submitHandlerId, onAmountEdit, isSubmitting, pendingEditRef]
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <section className="space-y-2">
      {showHeader && (
        <div>
          {!hideTitle && <h3 className="text-sm font-semibold tracking-tight text-foreground">{tableUi.title}</h3>}
          {tableUi.description && (
            <p className={cn('text-xs text-muted-foreground', !hideTitle && 'mt-0.5')}>{tableUi.description}</p>
          )}
        </div>
      )}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'h-10 bg-muted/40 text-xs uppercase tracking-wide',
                      header.column.id === 'amount' && 'text-right'
                    )}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableUi.columns.length} className="h-16 text-center text-muted-foreground">
                  No rows
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((tableRow) => {
                const row = tableRow.original
                return (
                  <TableRow
                    key={tableRow.id}
                    className={cn('hover:bg-muted/30', row.isTotal && 'bg-muted/70 hover:bg-muted/70')}
                  >
                    {tableRow.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'py-2 text-sm',
                          cell.column.id === 'amount' && 'text-right',
                          row.isTotal && cell.column.id === 'formula' && 'font-medium'
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      {showReferenceIndex && <BankReconReferenceIndex entries={referenceIndex} />}
    </section>
  )
}
